/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { createSelector } from '@reduxjs/toolkit';
import { sizes } from '@smartsheet/lodestar-core';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  setActiveTab,
  setshowRunStepsPanel,
} from '../../../../../services/ducks/designerDuck/designerDuck';
import {
  allRunsSelector,
  defaultFromTimestampSelector,
  fetchedAtleastOnceSelector,
  fetchWorkflowRunsList,
  filterSelector,
  fromRunSelector,
  hasNetworkErrorSelector,
  isWorkflowLoadingSelector,
  MAX_RECORDS,
  runsWereFoundSelector,
  setDefaultFromTimestamp,
  totalRunsSelector,
  userSetFromTimestampSelector,
  workflowUUIDSelector,
} from '../../../../../services/ducks/workflowDuck/workflowDuck';
import { PanelHeader } from '../../../../common/Panel/Panel';
import { runLogMenuOptions } from '../RunLogMenuOptions';
import { FilterNoResults } from './FilterNoResults';
import { List } from './List';
import { ListHeader } from './ListHeader';
import { ListLoadingState } from './ListLoadingState';
import { ListNoResults } from './ListNoResults';

enum WorkflowRunPanelView {
  EMPTY,
  ONE,
  LIST,
  LIST_PENDING,
  LIST_NETWORK_ERROR,
  LIST_EMPTY,
}

const runListContainerStyle = css`
  padding-right: ${sizes.large}px;
`;

function hasPageChanged(runsFetched: number, runsToBeFetched: number) {
  return runsFetched < runsToBeFetched;
}

function isLastPage(runsFetched: number, totalAvailableRuns: number) {
  return runsFetched == totalAvailableRuns;
}

function shouldFetchMoreWorkflowRuns(
  runsFetched: number,
  runsToBeFetched: number,
  totalAvailableRuns: number
) {
  return (
    hasPageChanged(runsFetched, runsToBeFetched) &&
    !isLastPage(runsFetched, totalAvailableRuns)
  );
}

const activeViewSelector = createSelector(
  allRunsSelector,
  isWorkflowLoadingSelector,
  hasNetworkErrorSelector,
  runsWereFoundSelector,
  (allRuns, isWorkflowLoading, hasNetworkError, runsWereFound) => {
    if (isWorkflowLoading) {
      return WorkflowRunPanelView.LIST_PENDING;
    }

    if (hasNetworkError) {
      return WorkflowRunPanelView.LIST_NETWORK_ERROR;
    }

    if (allRuns.length === 0) {
      if (runsWereFound) return WorkflowRunPanelView.LIST_EMPTY;
      // Only empty because a filter
      else return WorkflowRunPanelView.EMPTY; // Empty because there are no runs
    }

    return WorkflowRunPanelView.LIST;
  }
);

const renderRunLogPanel = (activeView: WorkflowRunPanelView) => {
  switch (activeView) {
    case WorkflowRunPanelView.LIST_PENDING:
      return <ListLoadingState />;
    case WorkflowRunPanelView.LIST_NETWORK_ERROR:
      return <ListNoResults />;
    case WorkflowRunPanelView.LIST_EMPTY:
      return <FilterNoResults />;
    case WorkflowRunPanelView.EMPTY:
      return <ListNoResults />;
    case WorkflowRunPanelView.LIST:
      return <List maxRecords={MAX_RECORDS} />;
    // NOTE: What should be the default case here? Is there any way a user can reach this state? Do we need unit tests to simulate this?
    default:
      return <ListNoResults />;
  }
};

export const RunList = () => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const activeView = useSelector(activeViewSelector);
  const workflowUUID = useSelector(workflowUUIDSelector);
  const userSetFromTimestamp = useSelector(userSetFromTimestampSelector);
  const defaultFromTimestamp = useSelector(defaultFromTimestampSelector);
  const filter = useSelector(filterSelector);
  const fromRun = useSelector(fromRunSelector);
  const allRuns = useSelector(allRunsSelector);
  const totalRuns = useSelector(totalRunsSelector);
  const fetchedAtleastOnce = useSelector(fetchedAtleastOnceSelector);

  useEffect(() => {
    if (!userSetFromTimestamp.length && !defaultFromTimestamp.length) {
      dispatch(setDefaultFromTimestamp(Date.now().toString()));
    }
  }, [userSetFromTimestamp.length, defaultFromTimestamp.length, dispatch]);

  useEffect(() => {
    if (
      shouldFetchMoreWorkflowRuns(
        allRuns.length,
        fromRun + MAX_RECORDS,
        totalRuns
      )
    ) {
      dispatch(fetchWorkflowRunsList());
    }
  }, [fromRun, dispatch, allRuns.length, totalRuns]);

  useEffect(() => {
    if (
      (userSetFromTimestamp.length || defaultFromTimestamp.length) &&
      !fetchedAtleastOnce
    ) {
      dispatch(fetchWorkflowRunsList());
    }
  }, [
    workflowUUID,
    filter,
    userSetFromTimestamp.length,
    defaultFromTimestamp.length,
    fetchedAtleastOnce,
    dispatch,
  ]);

  const closePanel = () => {
    batch(() => {
      dispatch(setActiveTab(null));
      dispatch(setshowRunStepsPanel(false));
    });
  };

  return (
    <div css={runListContainerStyle}>
      <PanelHeader
        title={translate('common:runLog')}
        onClose={closePanel}
        options={runLogMenuOptions}
      />
      <ListHeader />
      {renderRunLogPanel(activeView)}
    </div>
  );
};
